<template>
  <form @submit.prevent="() => {}">
    <FormInputGroup label="Name" for-id="name">
      <FormInput
        id="name"
        ref="nameInput"
        v-model:value="inputName"
        type="text"
        placeholder="Type a name..."
        :state="nameMissing || nameDuplicate ? 'error' : 'default'"
        @update:value="setNameFilled"
      />
      <BaseAlert v-if="nameMissing" state="error">
        Name is required.
      </BaseAlert>
      <BaseAlert v-if="nameDuplicate" state="error">
        A field with this name already exists in this type.
      </BaseAlert>
    </FormInputGroup>

    <FormInputGroup label="Relation Type" for-id="relation">
      <BaseInputSelect
        :value="relationSelectDisplay"
        :options="relationSelectOptions"
        placeholder="Search for a type…"
        :debounce-ms="0"
        select-only
        searchable
        size="form"
        @selected="onRelationSelected"
      >
        <template #selected-icon="{ value: tableId }">
          <BaseType
            state="img"
            :image="getTypeIcon(profile, getDatabase(tableId))"
            :color="getTypeColor(getDatabase(tableId))"
            size="xs"
            :clickable="false"
          />
        </template>
        <template #item="{ option }">
          <BaseInputSelectOptionItem :title="option.label">
            <template #icon>
              <BaseType
                state="img"
                size="xs"
                :image="getTypeIcon(profile, getDatabase(option.value))"
                :color="getTypeColor(getDatabase(option.value))"
              />
            </template>
          </BaseInputSelectOptionItem>
        </template>
      </BaseInputSelect>
      <BaseAlert v-if="relationMissing" state="error">
        Relation is required.
      </BaseAlert>
    </FormInputGroup>

    <CollapsibleSection header="Advanced">
      <FormInputGroup label="Description" for-id="description">
        <FormInput
          id="description"
          v-model:value="desc"
          type="text"
          placeholder="Describe your property..."
          state="default"
        />
      </FormInputGroup>

      <FormInputGroup label="Plural" for-id="plural">
        <FormInput
          id="plural"
          v-model:value="desc"
          type="text"
          placeholder="Plural name..."
          state="default"
        />
      </FormInputGroup>

      <FormInputGroup label="Default" for-id="default">
        <!--        <VSelect
          :clearable="false"
          :searchable="false"
          :options="relationOptions"
          v-model:value="defaultRelation"
        >
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon /></span>
          </template>
        </VSelect>-->
      </FormInputGroup>

      <FormInputGroup label="Constraints" for-id="constraints">
        <BaseToggleButton
          label="Required"
          help="This field is required"
          :checked="required"
          @change="(e) => toggleRequired(e)"
        />
        <BaseToggleButton
          label="Limit to 1"
          help="Only one option can be selected"
          :checked="single"
          @change="(e) => toggleSingle(e)"
        />
      </FormInputGroup>
    </CollapsibleSection>
  </form>
</template>

<script lang="ts">
import { upperFirst } from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { GraphRelationTarget, TableId } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseInputSelect, {
  SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import BaseType from "@/components/base/BaseType.vue";
import { getTypeColor, getTypeIcon } from "@/components/databases";
import CollapsibleSection from "@/components/graph/definition/form/CollapsibleSection.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import * as inject from "@/components/inject";
import { Filled } from "@/components/settings/TypeEdition.vue";

export default defineComponent({
  components: {
    BaseAlert,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    BaseType,
    BaseToggleButton,
    CollapsibleSection,
    FormInput,
    FormInputGroup,
  },
  props: {
    name: { type: String, required: true },
    description: { type: String, default: "" },
    relation: { type: String as PropType<TableId>, required: false },
    filled: { type: Object as PropType<Filled>, required: true },
    optional: { type: Boolean, required: true },
    list: { type: Boolean, required: true },
    workspaceId: { type: String, required: true },
    targetDatabases: {
      type: Array as PropType<GraphRelationTarget[]>,
      required: true,
    },
    existingNames: { type: Array as PropType<string[]>, default: () => [] },
  },
  emits: {
    updateName: (_name: string) => true,
    updateDescription: (_description: string) => true,
    updateRelation: (_database: TableId | undefined) => true,
    updateOptional: (_optional: boolean) => true,
    setNameFilled: () => true,
    setRelationFilled: () => true,
    updateList: (_list: boolean) => true,
  },
  setup(props, { emit }) {
    const {
      filled,
      optional,
      relation,
      name,
      description,
      list,
      targetDatabases,
      existingNames,
    } = toRefs(props);
    const profile = inject.profile();
    const nameInput = ref<InstanceType<typeof FormInput>>();

    onMounted(() => {
      nameInput.value?.focus();
    });

    const availableDatabases = computed<GraphRelationTarget[]>(
      () => targetDatabases.value
    );

    const availableDatabasesById = computed<Map<TableId, GraphRelationTarget>>(
      () => {
        const m = new Map<TableId, GraphRelationTarget>();
        for (const db of availableDatabases.value) {
          m.set(db.id, db);
        }
        return m;
      }
    );

    const database = computed<TableId | undefined>({
      get: () =>
        relation.value && availableDatabasesById.value.has(relation.value)
          ? relation.value
          : undefined,
      set: (id: TableId | undefined) => {
        setRelationFilled();
        emit("updateRelation", id);
      },
    });
    const getDatabase = (id: TableId) => availableDatabasesById.value.get(id);

    const relationTypeLabel = (db: GraphRelationTarget) =>
      db.data_name || db.name;

    const relationSelectOptions = computed<SelectOption[]>(() =>
      availableDatabases.value.map((db: GraphRelationTarget) => {
        const label = relationTypeLabel(db);
        const aliases: string[] = [];
        if (db.name && db.name !== label) {
          aliases.push(db.name);
        }
        return {
          value: db.id,
          label,
          aliases,
        };
      })
    );

    const relationSelectDisplay = computed(() => {
      const id = database.value;
      const db = id === undefined ? undefined : getDatabase(id);
      const raw = db === undefined ? "" : relationTypeLabel(db);
      return upperFirst(raw);
    });

    const onRelationSelected = (opt: SelectOption) => {
      database.value = opt.value satisfies TableId;
    };
    const inputName = computed({
      get() {
        return name.value;
      },
      set(newName: string) {
        emit("updateName", newName);
      },
    });
    const nameMissing = computed<boolean>(() => !filled.value.name);
    const nameDuplicate = computed<boolean>(() =>
      name.value ? existingNames.value.includes(name.value) : false
    );
    const relationMissing = computed<boolean>(() => !filled.value.relation);

    const setNameFilled = () => emit("setNameFilled");
    const setRelationFilled = () => emit("setRelationFilled");

    const desc = computed({
      get() {
        return description.value;
      },
      set(newDesc: string) {
        emit("updateDescription", newDesc);
      },
    });
    const required = computed<boolean>({
      get() {
        return !optional.value;
      },
      set(required: boolean) {
        emit("updateOptional", !required);
      },
    });
    const toggleRequired = (requiredStatus: boolean) => {
      required.value = requiredStatus;
    };
    const single = ref(!list.value);
    const toggleSingle = (enabled: boolean) => {
      single.value = enabled;
      emit("updateList", !single.value);
    };

    const plural = ref("");
    const defaultRelation = ref("");
    return {
      getDatabase,
      getTypeColor,
      getTypeIcon,
      profile,
      relationTypeLabel,
      defaultRelation,
      onRelationSelected,
      relationSelectDisplay,
      relationSelectOptions,
      desc,
      inputName,
      plural,
      relationMissing,
      required,
      setRelationFilled,
      setNameFilled,
      single,
      nameMissing,
      nameDuplicate,
      toggleRequired,
      toggleSingle,
      nameInput,
    };
  },
});
</script>

<style lang="scss" scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

:deep(.title) {
  text-transform: capitalize;
}
</style>
